import classNames from 'classnames';
import type { ReactNode } from 'react';
import { defineMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import type { So5LeaderboardType } from '@sorare/core/src/__generated__/globalTypes';
import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LandingPromoBanner } from '@sorare/core/src/components/Season2026/LandingPromoBanner';
import { Ambassadors } from '@sorare/core/src/components/landing/Ambassadors';
import { CommunityBlock } from '@sorare/core/src/components/landing/CommunityBlock';
import { Play } from '@sorare/core/src/components/landing/Season2026/Play';
import { Scout } from '@sorare/core/src/components/landing/Season2026/Scout';
import type { Player } from '@sorare/core/src/components/landing/Season2026/WalletLandingHero';
import { WalletLandingHero } from '@sorare/core/src/components/landing/Season2026/WalletLandingHero';
import type { RewardMarqueeAsset } from '@sorare/core/src/components/landing/Season2026/Win';
import { Win } from '@sorare/core/src/components/landing/Season2026/Win';
import type {
  League,
  Pictures,
} from '@sorare/core/src/components/landing/types';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { glossary } from '@sorare/core/src/lib/glossary';
import { breakpoints } from '@sorare/core/src/style/mediaQuery';
import { LeagueImage } from '@sorare/gameplay/src/components/league/LeagueImage';

import { FAQ } from '#src/components/landing/faq/index.tsx';

import thumbnailBundesliga from './assets/marquee/thumbnail_bundesliga.webp';
import thumbnailLaliga from './assets/marquee/thumbnail_laliga.webp';
import thumbnailLigue1 from './assets/marquee/thumbnail_ligue1.webp';
import thumbnailEpl from './assets/marquee/thumbnail_premierleague.webp';
import thumbnailU23 from './assets/marquee/thumbnail_u23.webp';

const Root = styled(Vertical)`
  background: var(--c-black);
  padding: var(--double-unit) 0;
  &.showFAQ {
    padding-bottom: calc(8 * var(--unit));
  }
`;

const leaguesAssets = [
  {
    id: 'u23',
    pictureUrl: thumbnailU23,
    headerUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-under_twenty_one/header.svg',
  },
  {
    id: 'seasonal-spain',
    pictureUrl: thumbnailLaliga,
    headerUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-spain/header.svg',
  },
  {
    id: 'seasonal-england',
    pictureUrl: thumbnailEpl,
    headerUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-england/header.svg',
  },
  {
    id: 'seasonal-germany',
    pictureUrl: thumbnailBundesliga,
    headerUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-germany/header.svg',
  },
  {
    id: 'ligue-1',
    pictureUrl: thumbnailLigue1,
    headerUrl:
      'https://frontend-assets.sorare.com/football/so5_league/first_division-france/header.svg',
  },
];

type Assets = {
  heroAssets: {
    background: Pictures;
    players: Player[];
  };
  scoutAssets: {
    background: Pictures;
    players: Pictures;
  };
  playAssets: {
    background: Pictures;
    lineup: Pictures;
  };
  winAssets: RewardMarqueeAsset[];
};

type Props = {
  leaderboards: So5LeaderboardType[];
  showFAQ?: boolean;
  assets: Assets;
  leagueLogo?: ReactNode;
  league: League;
};

const heroMessages = {
  hero: glossary.landingHero,
  heroWallet: glossary.landingHeroWallet,
  heroWalletPlaceholder: glossary.landingHeroWalletPlaceholder,
  heroDescription: defineMessage({
    defaultMessage:
      'Build your 5-player lineups. When they shine on the field, you earn rewards!',
  }),
};

const scoutMessages = defineMessages({
  scout: {
    defaultMessage: 'Scout like a pro',
  },
  scoutDescription: {
    defaultMessage: 'Over 300 officially licensed clubs and leagues.',
  },
  scoutButton: {
    defaultMessage: 'Create your team',
  },
});

const playMessages = defineMessages({
  play: {
    defaultMessage: 'Build your lineup',
  },
  playDescription: {
    defaultMessage:
      'Select your top players each Game Week. Their scores reflect their real-life performances.',
  },
});

const winMessages = {
  ...defineMessages({
    win: {
      defaultMessage: 'Win Epic Rewards',
    },
    winDescription: {
      defaultMessage:
        'Signed jerseys, match tickets, VIP experiences, cash or ETH all yours to win! You might even meet the G.O.A.T of managers: Zinedine Zidane!',
    },
  }),
  marquee: glossary.landingHero,
};

export const WalletLanding = ({
  leaderboards,
  showFAQ,
  assets,
  leagueLogo,
  league,
}: Props) => {
  const {
    flags: { useLandingPagePromoBanner = false },
  } = useFeatureFlags();

  return (
    <Root
      gap={8}
      className={classNames({ extraBottomPadding: !showFAQ }, 'dark-mode')}
    >
      <Vertical>
        {!!useLandingPagePromoBanner && (
          <Container maxWidth={breakpoints.mediumDesktop}>
            <LandingPromoBanner league={league} />
          </Container>
        )}
        <section>
          <Vertical gap={4}>
            <WalletLandingHero
              assets={assets.heroAssets}
              leagueLogo={leagueLogo}
              league={league}
              messages={heroMessages}
            />
          </Vertical>
        </section>
      </Vertical>
      <section>
        <Scout
          assets={assets.scoutAssets}
          league={league}
          messages={scoutMessages}
        >
          {leaguesAssets.map(({ id, ...assetProps }) => (
            <LeagueImage key={id} {...assetProps} />
          ))}
        </Scout>
      </section>
      <section>
        <Play
          assets={assets.playAssets}
          league={league}
          messages={playMessages}
        />
      </section>
      <section>
        <Win league={league} assets={assets.winAssets} messages={winMessages} />
      </section>
      <section style={{ marginTop: 'calc(var(--unit) * -20)' }}>
        <CommunityBlock
          leaderboards={leaderboards.map(leaderboardType => ({
            sport: Sport.FOOTBALL,
            leaderboardType,
          }))}
        />
      </section>
      <section>
        <Container>
          <Ambassadors />
        </Container>
      </section>
      {!!showFAQ && <FAQ />}
    </Root>
  );
};
